/*
 * hpq-draft-checkout.js — routes cart checkout through a worker-created draft order.
 *
 * Why: on this non-Plus store the native checkout shows a discount-code field. A code
 * pasted there can drop the order below the $59 free-ship line AFTER the cart already
 * promised "FREE SHIPPING" — the "$9 surprise". Routing checkout through a draft order
 * (a) hides the discount field (draft invoices don't show it) so codes stay cart-only,
 * and (b) lets the worker force free shipping whenever the pre-coupon subtotal already
 * earned it, so the cart bar and checkout always agree.
 *
 * Contract with the worker (POST {workerBase}/checkout/create-draft):
 *   body    -> { items: [{ variantId: "gid://shopify/ProductVariant/<id>", quantity }],
 *                appliedCode?: string, customerId?: "gid://shopify/Customer/<id>" }
 *   200 ok  -> { ok:true, serverProjection:{ invoiceUrl } }  => navigate to the invoice
 *   422     -> discount_code_rejected (Shopify refused the code: expired / already used /
 *              wrong customer / under the $20 minimum). We must NOT fall back to native
 *              here — native would silently charge full price without the code. Instead we
 *              fire "hpq:draft-code-rejected" so the coupon box can tell the shopper and
 *              let them remove/retry, and we stay on the cart.
 *   anything else / timeout / network error => fall back to native /checkout (the safety
 *              net; native still has its own discount field for a manual retry).
 *
 * All behavior is gated behind window.HPQ_CONFIG.draftCheckoutEnabled (host-defaulted in
 * theme-globals.liquid: on for staging, off for prod) or a live window.HPQ_FORCE_DRAFT_CHECKOUT.
 */
(function () {
  'use strict';

  // Two sequential Admin API round-trips on the worker for a coupon cart (calculate, then
  // create); one for a plain cart. This is the fallback CEILING, not the target: in the
  // common case the redirect happens well before it. It must exceed worst-case *successful*
  // latency (coupon cart on a cold worker) so we never strand a working draft into native
  // checkout — where the coupon would be dropped and the discount field would reappear.
  // Measured: ~2.4s warm / ~3.4s cold (plain); coupon carts add a round-trip. 6s gives margin.
  // TODO(4sm): once the worker keeps its admin token warm, this can come back down.
  var TIMEOUT_MS = 6000;

  // One-shot latch: a double-click on Checkout must not create two draft orders (one would
  // be orphaned in the admin) or fire two navigations. Reset only on the non-navigating 422
  // path so the shopper can retry after fixing the code.
  var going = false;

  function enabled() {
    return (window.HPQ_CONFIG && window.HPQ_CONFIG.draftCheckoutEnabled === true) ||
      window.HPQ_FORCE_DRAFT_CHECKOUT === true;
  }

  function workerBase() {
    return (window.HPQ_CONFIG && window.HPQ_CONFIG.workerBase) || '';
  }

  // The worker derives the customer from this token and IGNORES any customerId in
  // the body — sending one was how a draft order could be attached to a stranger's
  // account. A signed-out shopper simply sends no header and checks out anonymously.
  function caaAuthHeaders(base) {
    var out = {};
    Object.keys(base || {}).forEach(function (k) { out[k] = base[k]; });
    try {
      var api = window.HPQCustomerAccountAPI;
      var t = (api && typeof api.getCachedTokens === 'function') ? api.getCachedTokens() : null;
      if (t && t.accessToken) out['X-CAA-Access-Token'] = t.accessToken;
    } catch (e) { /* never block a checkout on a token read */ }
    return out;
  }

  function nativeCheckout() {
    window.location.href = '/checkout';
  }

  // Build the draft line items from the local cart. The local cart carries only the numeric
  // Shopify variant id (as a string); the Admin API wants a ProductVariant GID, which we
  // synthesize here. Anything without a purely-numeric id or a positive quantity is dropped
  // rather than sent as a malformed line the endpoint would 400 on.
  function buildItems() {
    var cart = window.hpqCart;
    var raw = (cart && typeof cart.getItems === 'function') ? cart.getItems() : [];
    var items = [];
    for (var i = 0; i < raw.length; i++) {
      var vid = String(raw[i].variant_id || '');
      var qty = parseInt(raw[i].quantity, 10);
      if (/^\d+$/.test(vid) && qty >= 1) {
        items.push({ variantId: 'gid://shopify/ProductVariant/' + vid, quantity: qty });
      }
    }
    return items;
  }

  // Customer GID, present only after a Customer-Account-API (PKCE) login. The fortune code
  // is customer-scoped, so passing this lets the draft's purchasingEntity match the code's
  // customer (without it, Shopify would refuse a scoped code). Absent => omit; a code-less
  // cart doesn't need it.
  function customerGid() {
    try {
      var api = window.HPQCustomerAccountAPI;
      var c = (api && typeof api.getCachedCustomer === 'function') ? api.getCachedCustomer() : null;
      return (c && c.gid) || '';
    } catch (e) {
      return '';
    }
  }

  function go(opts) {
    opts = opts || {};
    if (going) return Promise.resolve();
    if (!enabled() || !workerBase()) {
      nativeCheckout();
      return Promise.resolve();
    }

    var items = buildItems();
    if (!items.length) {
      // Empty/unsyncable cart — let the platform handle it natively.
      nativeCheckout();
      return Promise.resolve();
    }

    var body = { items: items };
    // The checkout gate passes the full applied-code list; fall back to our own state.
    var codes = (Array.isArray(opts.appliedCodes) ? opts.appliedCodes : getCodes()).filter(Boolean);
    if (codes.length) body.appliedCodes = codes;
    // A customer-scoped code needs its customer on the draft or Shopify refuses it — but
    // the worker now resolves that customer from the CAA access token, not from anything
    // this client asserts. Sending a customerId here is how a draft order could be
    // attached to a stranger's account.

    going = true;
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS);
    var cleared = false;
    function clear() { if (!cleared) { cleared = true; clearTimeout(timer); } }

    return fetch(workerBase() + '/checkout/create-draft', {
      method: 'POST',
      headers: caaAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
      signal: ctrl.signal
    })
      .then(function (r) {
        clear();
        // Shopify refused the code — do NOT native-fallback (that drops the code and
        // charges full price). Surface it so the coupon box can message + let them retry.
        if (r.status === 422) {
          going = false; // stayed on the cart — allow a retry after they fix/remove the code
          // `codes` is the applied list; `code` (singular) was never defined here, so building this
          // detail threw a ReferenceError BEFORE dispatch — the event never fired, the promise
          // rejected into .catch(), and the shopper was silently sent to native /checkout at FULL
          // price (the exact "do NOT native-fallback" case this branch exists to prevent).
          //
          // The worker names the codes it refused, which matters once more than one is applied:
          // without it we'd tell the shopper "a code failed" and leave them to guess which.
          // Parsing must never be able to break this branch — falling back to the whole applied
          // list is a worse message, not a broken checkout.
          //
          // Everything below is inside a try/catch on purpose. This branch sits upstream of an
          // outer .catch that calls nativeCheckout(), so ANY throw here would send the shopper
          // to native checkout at full price — the precise failure this branch exists to
          // prevent, and the one a ReferenceError caused here once already. Staying on the cart
          // with a vaguer message is always the better failure.
          return r.json().catch(function () { return null; }).then(function (j) {
            try {
              var fromWorker = j && j.serverProjection && j.serverProjection.rejectedCodes;
              var rejected = (Object.prototype.toString.call(fromWorker) === '[object Array]' &&
                fromWorker.length) ? fromWorker : codes;
              document.dispatchEvent(new CustomEvent('hpq:draft-code-rejected', {
                detail: { code: rejected[0] || '', codes: rejected, signedIn: !!customerGid() }
              }));
            } catch (err) { /* never let a message fail the refusal into native checkout */ }
          });
        }
        if (!r.ok) { nativeCheckout(); return; }
        return r.json().then(function (j) {
          var url = j && j.ok && j.serverProjection && j.serverProjection.invoiceUrl;
          if (url) { window.location.href = url; }
          else { nativeCheckout(); }
        });
      })
      .catch(function () {
        clear();
        // Timeout / network / parse failure — native checkout is the safety net.
        nativeCheckout();
      });
  }

  window.hpqDraftCheckout = { go: go, getCoupons: getCoupons, getCodes: getCodes };

  // ── Cart coupons: array state + persistence + express-button suppression + reject notice ──
  //
  // window.hpqAppliedCoupons is the source of truth: an array of applied codes the checkout
  // gate hands to the draft as appliedCodes[]. Each entry: { code, pct, exp, customerId }.
  // A Shopify draft applies whatever stacks (order-level codes rarely combine, so this is
  // usually one entry) and validates the rest. window.hpqAppliedCoupon stays as a back-compat
  // alias to the first entry for any older reader.
  //
  // Persisted to sessionStorage so it survives navigation — a shopper who applies a code,
  // keeps browsing, then checks out must not create a code-less draft and pay full price.
  var COUPON_KEY = 'hpq_applied_coupons';
  var LEGACY_KEY = 'hpq_applied_coupon';

  window.hpqAppliedCoupons = Array.isArray(window.hpqAppliedCoupons) ? window.hpqAppliedCoupons : [];

  function syncAlias() { window.hpqAppliedCoupon = window.hpqAppliedCoupons[0] || null; }
  function persist() {
    try { sessionStorage.setItem(COUPON_KEY, JSON.stringify(window.hpqAppliedCoupons)); } catch (e) {}
  }
  function dropStore() {
    try { sessionStorage.removeItem(COUPON_KEY); } catch (e) {}
    try { sessionStorage.removeItem(LEGACY_KEY); } catch (e) {}
  }
  function pruneExpired(list) {
    var now = Date.now();
    return (Array.isArray(list) ? list : []).filter(function (c) {
      return c && c.code && !(c.exp && c.exp <= now);
    });
  }
  function getCoupons() { return window.hpqAppliedCoupons.slice(); }
  function getCodes() { return window.hpqAppliedCoupons.map(function (c) { return c.code; }); }
  function addCoupon(c) {
    if (!c || !c.code) return;
    if (c.exp && c.exp <= Date.now()) return;
    var at = -1;
    for (var i = 0; i < window.hpqAppliedCoupons.length; i++) {
      if (window.hpqAppliedCoupons[i].code === c.code) { at = i; break; }
    }
    if (at !== -1) window.hpqAppliedCoupons[at] = c; // idempotent: same code replaces
    else window.hpqAppliedCoupons.push(c);
    syncAlias(); persist();
  }
  function removeCoupon(code) {
    if (code) {
      window.hpqAppliedCoupons = window.hpqAppliedCoupons.filter(function (c) { return c.code !== code; });
    } else {
      window.hpqAppliedCoupons = []; // no code => clear all
    }
    syncAlias();
    if (window.hpqAppliedCoupons.length) persist(); else dropStore();
  }

  function rehydrateCoupons() {
    try {
      var raw = sessionStorage.getItem(COUPON_KEY);
      var list = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(list)) {
        // Migrate a legacy single-coupon store from the pre-array version.
        var legacyRaw = sessionStorage.getItem(LEGACY_KEY);
        var legacy = legacyRaw ? JSON.parse(legacyRaw) : null;
        list = (legacy && legacy.code) ? [legacy] : [];
      }
      window.hpqAppliedCoupons = pruneExpired(list);
      syncAlias();
      if (window.hpqAppliedCoupons.length) persist(); else dropStore();
    } catch (e) {
      window.hpqAppliedCoupons = []; syncAlias(); dropStore();
    }
  }

  // Accelerated checkout buttons (Shop Pay / Apple Pay) bypass the click gate and go straight
  // to native checkout — for a coupon cart that means full price + the native discount field
  // is back. Hide them only while a cart coupon is applied (no-coupon carts keep express;
  // flag-off carts never populate the array, so this is inert there). We toggle a root class
  // matched by a CSS rule emitted inline in theme-globals.liquid, so hiding is in force from
  // first paint (no flash) and covers any express container rendered after load.
  function toggleExpress(hide) {
    // Flag-off must stay inert: never hide the native express buttons based on (possibly stale)
    // sessionStorage coupons when draft checkout is disabled, or a flag-ON→OFF rollback would
    // leave Shop Pay/Apple Pay hidden sitewide with no visible coupon. Rollback = fully inert.
    if (!enabled()) hide = false;
    try { document.documentElement.classList.toggle('hpq-coupon-active', hide); } catch (e) {}
  }

  // Guaranteed-visible refusal notice: the footer coupon slot may be collapsed or absent on
  // /cart, so we place the message directly above the checkout button the shopper just used.
  function showRejectNotice(msg) {
    var btn = document.querySelector('button[name="checkout"], a[href*="/checkout"]');
    var el = document.querySelector('[data-hpq-draft-notice]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-hpq-draft-notice', '');
      el.setAttribute('role', 'alert');
      el.style.cssText = 'margin:8px 0;padding:8px 10px;border-radius:6px;' +
        'background:#fff3f3;color:#8a1c1c;font-size:14px;line-height:1.35;';
      if (btn && btn.parentNode) { btn.parentNode.insertBefore(el, btn); }
      else { document.body.appendChild(el); }
    }
    el.textContent = msg;
  }

  document.addEventListener('hpq:coupon-applied', function (e) {
    var d = (e && e.detail) || {};
    if (d.code) addCoupon({ code: d.code, pct: d.pct, exp: d.exp || 0, customerId: d.customerId || '' });
    toggleExpress(window.hpqAppliedCoupons.length > 0);
    var n = document.querySelector('[data-hpq-draft-notice]');
    if (n && n.parentNode) n.parentNode.removeChild(n); // clear a stale refusal
  });
  document.addEventListener('hpq:coupon-removed', function (e) {
    // The module is the authority on removal. A detail.code removes that one code; an empty
    // detail clears all — so a removal dispatched by anyone (Remove button, superseded-code
    // drop, empty-cart below) fully detaches the code(s) from checkout.
    var d = (e && e.detail) || {};
    removeCoupon(d.code || '');
    toggleExpress(window.hpqAppliedCoupons.length > 0);
    var n = document.querySelector('[data-hpq-draft-notice]');
    if (n && n.parentNode) n.parentNode.removeChild(n);
  });
  document.addEventListener('hpq:draft-code-rejected', function (e) {
    // The refusal reasons widened when the worker started catching codes Shopify SILENTLY
    // drops (m5n.10) — a cart that fell under the code's minimum, or a scoped code with nobody
    // signed in. The old copy asserted "expired or already used" and told everyone to come back
    // tomorrow, which is actively wrong advice for both: one needs a bigger cart, the other a
    // login, and both are fixable right now. Shopify never tells us WHICH reason it was, so the
    // copy below states the possibilities honestly instead of guessing, and leads with the one
    // signal we can actually check.
    var d = (e && e.detail) || {};
    var named = (d.codes && d.codes.length === 1) ? '“' + d.codes[0] + '”' : 'that code';
    if (!d.signedIn) {
      showRejectNotice('We couldn\u2019t apply ' + named + ' \u2014 fortune codes are tied to ' +
        'your account. Sign in and try again, or remove it to check out.');
      return;
    }
    showRejectNotice('We couldn\u2019t apply ' + named + ' to this cart. It may have expired, ' +
      'been used already, or your cart may no longer meet its minimum. Adjust your cart and ' +
      'try again, or remove it to check out.');
  });

  // If the cart is emptied, drop the coupon so a stale code + customerId can't ride along to a
  // later, different cart. (The worker would 422 a bad code anyway, but a ghost code the
  // shopper can't see is worse UX than a clean reset.)
  document.addEventListener('cart:updated', function () {
    try {
      var c = (window.hpqCart && typeof window.hpqCart.getCart === 'function') ? window.hpqCart.getCart() : null;
      if (c && c.item_count === 0 && window.hpqAppliedCoupons.length) {
        document.dispatchEvent(new CustomEvent('hpq:coupon-removed', { detail: {} }));
      }
    } catch (e) {}
  });

  // NOTE on account-switch (customer A applies a code, logs out, customer B logs in): we do NOT
  // proactively drop coupons here. Every applied code carries the apply-time customerId (public
  // codes included), so dropping on a customerId mismatch would silently remove a valid PUBLIC
  // promo for B. Instead the existing hpq-cart-coupon.js handler re-quotes on the same
  // 'hpq:auth-state-changed' event and its auto-detach (gated on a live gid) removes only codes
  // Shopify actually rejects for B — dropping A's customer-scoped code while keeping B's valid
  // public one. At checkout the customer is no longer asserted by this client at all — the
  // worker reads it from the CAA access token — so A's stale cached gid can no longer ride
  // along on B's draft even in principle. customerIdForCodes() existed to resolve that
  // conflict and is gone with it.

  // BFCache: when the browser restores this page from history (Back after a checkout redirect),
  // scripts do NOT re-execute — the one-shot `going` latch stays true from the pre-navigation
  // attempt, silently deadlocking every later checkout click (go() early-returns). Reset it.
  // hpq-local-cart.js clears the disabled/spinner checkout UI on the same pageshow. Mirrors the
  // CAA module's pkceInFlight bfcache reset.
  window.addEventListener('pageshow', function (e) {
    if (e && e.persisted) going = false;
  });

  rehydrateCoupons();
  // Reconcile the express-hide class with the (possibly expired-and-pruned) rehydrated coupons.
  toggleExpress(window.hpqAppliedCoupons.length > 0);
})();
